import React, { useState } from 'react';
import { 
  HeartIcon, 
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
  ChevronLeftIcon
} from '@heroicons/react/24/outline';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';

interface HealthAssessmentWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (assessmentData: any) => Promise<void>;
  birdId: string;
  birdName: string;
}

interface AssessmentStep {
  id: string;
  title: string;
  description: string;
  questions: AssessmentQuestion[];
}

interface AssessmentQuestion {
  id: string;
  text: string;
  type: 'yes_no' | 'scale' | 'multiple_choice';
  options?: string[];
  required: boolean;
}

const HealthAssessmentWizard: React.FC<HealthAssessmentWizardProps> = ({
  isOpen,
  onClose,
  onSubmit,
  birdId,
  birdName
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);

  const assessmentSteps: AssessmentStep[] = [
    {
      id: 'visual',
      title: 'Visual Assessment',
      description: 'Check the bird\'s physical appearance and condition',
      questions: [
        {
          id: 'feathers',
          text: 'Are the feathers smooth, well-groomed, and clean?',
          type: 'yes_no',
          required: true
        },
        {
          id: 'eyes',
          text: 'Are the eyes bright, alert, and clear?',
          type: 'yes_no',
          required: true
        },
        {
          id: 'beak',
          text: 'Is the beak smooth, properly shaped, and normal color?',
          type: 'yes_no',
          required: true
        },
        {
          id: 'feet',
          text: 'Are the feet and legs clean, well-formed, and normal?',
          type: 'yes_no',
          required: true
        }
      ]
    },
    {
      id: 'behavioral',
      title: 'Behavioral Assessment',
      description: 'Observe the bird\'s behavior and activity level',
      questions: [
        {
          id: 'activity',
          text: 'Is the bird active and moving normally?',
          type: 'yes_no',
          required: true
        },
        {
          id: 'appetite',
          text: 'Is the bird eating normally and showing interest in food?',
          type: 'yes_no',
          required: true
        },
        {
          id: 'vocalization',
          text: 'Are vocalization patterns normal?',
          type: 'yes_no',
          required: true
        },
        {
          id: 'social',
          text: 'Is the bird interacting normally with other birds?',
          type: 'yes_no',
          required: true
        }
      ]
    },
    {
      id: 'physical',
      title: 'Physical Assessment',
      description: 'Check physical health indicators',
      questions: [
        {
          id: 'weight',
          text: 'Is the bird\'s weight within normal range?',
          type: 'yes_no',
          required: true
        },
        {
          id: 'droppings',
          text: 'Are droppings normal consistency and color?',
          type: 'yes_no',
          required: true
        },
        {
          id: 'breathing',
          text: 'Is breathing normal with no labored breathing?',
          type: 'yes_no',
          required: true
        }
      ]
    },
    {
      id: 'environmental',
      title: 'Environmental Assessment',
      description: 'Check environmental conditions',
      questions: [
        {
          id: 'temperature',
          text: 'Are temperature and humidity within appropriate range?',
          type: 'yes_no',
          required: true
        },
        {
          id: 'air_quality',
          text: 'Is air quality good with proper ventilation?',
          type: 'yes_no',
          required: true
        },
        {
          id: 'cage',
          text: 'Is the cage clean, well-maintained, and safe?',
          type: 'yes_no',
          required: true
        },
        {
          id: 'food_water',
          text: 'Are fresh food and clean water available?',
          type: 'yes_no',
          required: true
        }
      ]
    }
  ];

  const handleAnswer = (questionId: string, answer: any) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleNext = () => {
    if (currentStep < assessmentSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      await onSubmit({
        birdId,
        birdName,
        assessmentSteps,
        answers,
        completedAt: new Date().toISOString()
      });
      handleClose();
    } catch (error) {
      console.error('Error submitting health assessment:', error);
      alert('Failed to submit health assessment');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCurrentStep(0);
    setAnswers({});
    onClose();
  };

  const currentStepData = assessmentSteps[currentStep];
  const isLastStep = currentStep === assessmentSteps.length - 1;
  const isFirstStep = currentStep === 0;

  const getOverallHealthScore = () => {
    const totalQuestions = assessmentSteps.reduce((sum, step) => sum + step.questions.length, 0);
    const yesAnswers = Object.values(answers).filter(answer => answer === true).length;
    return Math.round((yesAnswers / totalQuestions) * 100);
  };

  const getHealthStatus = () => {
    const score = getOverallHealthScore();
    if (score >= 90) return { status: 'Excellent', color: 'text-green-600 bg-green-100' };
    if (score >= 75) return { status: 'Good', color: 'text-cyan-600 bg-blue-100' };
    if (score >= 50) return { status: 'Fair', color: 'text-yellow-600 bg-yellow-100' };
    return { status: 'Poor', color: 'text-red-600 bg-red-100' };
  };

  return (
    <BrandModal open={isOpen} onClose={handleClose} maxWidthClass="max-w-2xl">
      <BrandModalHeader
        title={
          <span className="inline-flex items-center gap-3">
            <HeartIcon className="h-6 w-6 text-red-500" />
            Health Assessment Wizard - {birdName}
          </span>
        }
        onClose={handleClose}
      />
      <BrandModalBody>
        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-slate-600 mb-2">
            <span>Step {currentStep + 1} of {assessmentSteps.length}</span>
            <span>{Math.round(((currentStep + 1) / assessmentSteps.length) * 100)}% Complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-cyan-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / assessmentSteps.length) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Current Step */}
        <div className="mb-6">
          <h4 className="text-lg font-medium text-slate-900 mb-2">{currentStepData.title}</h4>
          <p className="text-sm text-slate-600 mb-4">{currentStepData.description}</p>

          <div className="space-y-4">
            {currentStepData.questions.map((question) => (
              <div key={question.id} className="border border-gray-200 rounded-lg p-4">
                <h5 className="text-sm font-medium text-slate-900 mb-3">{question.text}</h5>
                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => handleAnswer(question.id, true)}
                    className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md ${
                      answers[question.id] === true
                        ? 'border-green-300 bg-emerald-100 text-emerald-800'
                        : 'border-gray-300 bg-white text-slate-700 hover:bg-slate-50/80'
                    }`}
                  >
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => handleAnswer(question.id, false)}
                    className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md ${
                      answers[question.id] === false
                        ? 'border-red-300 bg-rose-100 text-rose-800'
                        : 'border-gray-300 bg-white text-slate-700 hover:bg-slate-50/80'
                    }`}
                  >
                    <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                    No
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Health Score Summary */}
        {Object.keys(answers).length > 0 && (
          <div className={`p-4 rounded-lg border ${getHealthStatus().color} mb-2`}>
            <h4 className="text-sm font-medium mb-2">Current Health Score</h4>
            <p className="text-sm">{getOverallHealthScore()}% - {getHealthStatus().status}</p>
          </div>
        )}
      </BrandModalBody>
      <BrandModalFooter className="justify-between">
        <button
          type="button"
          onClick={handlePrevious}
          disabled={isFirstStep}
          className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeftIcon className="h-4 w-4 mr-2" />
          Previous
        </button>

        {isLastStep ? (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="btn btn-primary disabled:opacity-50"
          >
            {loading ? 'Submitting...' : 'Complete Assessment'}
          </button>
        ) : (
          <button type="button" onClick={handleNext} className="btn btn-primary">
            Next
            <ChevronRightIcon className="h-4 w-4 ml-2" />
          </button>
        )}
      </BrandModalFooter>
    </BrandModal>
  );
};

export default HealthAssessmentWizard;
